import { Effect } from 'effect';

/**
 * W.4.115 — a paid visitor lands on the tool for their keyword.
 *
 * Every Google Ads keyword used to land on the `/tools` index, where 52% of
 * paid visitors opened no tool (#2685). The ad link carries the keyword as
 * `utm_term`. This module maps it to one tool slug, so the Worker can send the
 * visitor to `/tools/<slug>` with the whole query string (gclid and the UTM
 * fields stay for attribution). A keyword with no single tool stays on the
 * index, which then shows the likely tools first.
 *
 * Editor keywords (`pdf editor`, `edit document online`) map to no converter:
 * the index shows the Studio editor card for them (#2686).
 */

/** Where the index's "Edit a PDF" card opens: the Studio editor with Edit text selected. */
export const STUDIO_EDITOR_HREF = '/studio?tool=edit-text';

const FROM_PDF = String.raw`\bpdf (?:to|into|2)`;
const TO_PDF = String.raw`(?:to|into|2) pdf\b`;

/**
 * First match wins. Conversions come before the verbs, so `merge jpg to pdf`
 * is a conversion and `compress pdf image` is a compression.
 */
const KEYWORD_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [new RegExp(`${FROM_PDF} (?:jpe?g|images?|pictures?|photos?)\\b`), 'pdf-to-jpg'],
  [new RegExp(`${FROM_PDF} png\\b`), 'pdf-to-png'],
  [new RegExp(`${FROM_PDF} (?:excel|xlsx?|spreadsheets?|csv)\\b`), 'pdf-to-excel'],
  [new RegExp(`${FROM_PDF} (?:word|docx?)\\b`), 'pdf-to-word'],
  [new RegExp(`${FROM_PDF} (?:markdown|md)\\b`), 'pdf-to-markdown'],
  [new RegExp(`${FROM_PDF} json\\b`), 'pdf-to-json'],
  [new RegExp(`${FROM_PDF} html?\\b`), 'pdf-to-html'],
  [new RegExp(`\\bpng ${TO_PDF}`), 'png-to-pdf'],
  [new RegExp(`\\b(?:jpe?g|jfif|images?|pictures?|photos?|pics?|img) ${TO_PDF}`), 'jpg-to-pdf'],
  [new RegExp(`\\b(?:word|docx?) ${TO_PDF}`), 'word-to-pdf'],
  [new RegExp(`\\b(?:excel|xlsx?) ${TO_PDF}`), 'excel-to-pdf'],
  [/\b(?:merge|combine|join)\b/, 'merge'],
  [/\b(?:split|separate)\b/, 'split'],
  [/\b(?:compress|compressor|shrink|reduce)\b/, 'compress'],
  [/\brotate\b/, 'rotate'],
  [/\bredact\b/, 'redact'],
  [/\bwatermark\b/, 'watermark'],
  [/\bpage numbers?\b/, 'page-numbers'],
  [/\b(?:sign|signature|esign)\b/, 'sign'],
  [/\bocr\b/, 'ocr'],
  [/\bcrop\b/, 'crop'],
  [/\bflatten\b/, 'flatten'],
];

const EDITOR_KEYWORD = /\bedit(?:or|ing|s)?\b/;
const CONVERTER_KEYWORD = /\bconver(?:t|ter|ters|sion)\b/;

/** For a keyword with no single tool: the three tools to show above the grid. */
const CONVERTER_SUGGESTIONS = ['jpg-to-pdf', 'word-to-pdf', 'pdf-to-word'] as const;
const GENERAL_SUGGESTIONS = ['jpg-to-pdf', 'merge', 'compress'] as const;

/** `jpg+to+pdf`, ` JPG-to-PDF ` and `jpg to pdf` all become `jpg to pdf`. */
function normalizeKeywordInternal(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .slice(0, 100);
}

function isEditorKeywordInternal(keyword: string): boolean {
  return EDITOR_KEYWORD.test(keyword);
}

function keywordToolInternal(raw: string | null | undefined): string | null {
  const keyword = normalizeKeywordInternal(raw);
  // An editor searcher wants the editor, not the converter a word in the
  // keyword happens to name (`edit and sign pdf`).
  if (!keyword || isEditorKeywordInternal(keyword)) return null;
  for (const [pattern, slug] of KEYWORD_RULES) {
    if (pattern.test(keyword)) return slug;
  }
  return null;
}

export const keywordToolEffect = Effect.fn('web.tools.keywordTool')(function* (
  raw: string | null | undefined,
) {
  return keywordToolInternal(raw);
});

/** The tool slug for an ad keyword, or null when no single tool fits it. */
export function keywordTool(raw: string | null | undefined): string | null {
  return Effect.runSync(keywordToolEffect(raw));
}

/** True for a keyword that asks for an editor (`pdf editor`, `edit document online`). */
export function isEditorKeyword(raw: string | null | undefined): boolean {
  return isEditorKeywordInternal(normalizeKeywordInternal(raw));
}

/**
 * The tools the index shows first for a keyword that maps to no single tool.
 * Empty when there is no keyword, the keyword has its own tool, or it asks for
 * the editor (the editor card covers it).
 */
export function keywordSuggestions(raw: string | null | undefined): readonly string[] {
  const keyword = normalizeKeywordInternal(raw);
  if (!keyword || isEditorKeywordInternal(keyword) || keywordToolInternal(keyword)) return [];
  return CONVERTER_KEYWORD.test(keyword) ? CONVERTER_SUGGESTIONS : GENERAL_SUGGESTIONS;
}

export const keywordToolRedirectEffect = Effect.fn('web.tools.keywordToolRedirect')(function* (
  url: URL,
) {
  if (url.pathname !== '/tools' && url.pathname !== '/tools/') return null;
  const slug = keywordToolInternal(url.searchParams.get('utm_term'));
  return slug ? `/tools/${slug}${url.search}` : null;
});

/**
 * `/tools?utm_term=jpg+to+pdf&gclid=…` → `/tools/jpg-to-pdf?utm_term=jpg+to+pdf&gclid=…`.
 * The query string is kept byte for byte. Null when the URL stays on the index.
 */
export function keywordToolRedirect(url: URL): string | null {
  return Effect.runSync(keywordToolRedirectEffect(url));
}

/**
 * The Worker's answer for a keyword landing: a temporary redirect (the map can
 * change, so no browser or cache keeps it), or null.
 */
export function keywordToolRedirectResponse(url: URL): Response | null {
  const location = keywordToolRedirect(url);
  if (!location) return null;
  return new Response(null, {
    status: 302,
    headers: { Location: location, 'Cache-Control': 'no-store' },
  });
}
